/**
 * Happy-flow modal save orchestration for the single-order header.
 * @module headerModals/hooks/useHappyFlowModalSave
 */

import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { markOrderAsCompleted } from "../../../../../api/postCall/commentRpaModalSaveForOrderIds";
import { OrderReassignmentPayload } from "../../../../../components/modals/reAssignModal/types";
import { ToastMessages } from "../../../../../components/toasts/toastMessages";
import { useBusniessOfficeNotesContext } from "../../../../../context/busniessOfficeNotesContext";
import {
  AuthOnFile,
  GeneralOrderDetails,
  JCodeDetails,
  SingleCoverage,
} from "../../../../../data-model/medicalPaOrdersModel";
import { ModalNamesForBigQuery } from "../../../../../enums/eventsData";
import { SingleOrderDetailsTableName } from "../../../../../enums/singleOrderDetailsTableName";
import { useUpdateOrderDetails } from "../../../../../hooks/singleMedicalPaOrderDetails/useUpdateOrderDetails";
import { useAssignedToUpdate } from "../../../../../pages/worklists/medicalPaOrdersAlgolia/hooks/useAssignedToUpdate";
import { AppDispatch, RootState } from "../../../../../redux/store/store";
import { getOrgIdForFetchExternalWorklist } from "../../../../../utils/organizationHelper";
import { showApiError } from "../../../../../utils/showApiError";
import { SelectedProviderRef } from "../types";
import { fireMarkAsCompletedAnalytics } from "../utils/fireMarkAsCompletedAnalytics";
import { hasProviderSelection } from "../utils/hasProviderSelection";
import { navigateToWorklistsAfterMarkComplete } from "../utils/navigateToWorklistsAfterMarkComplete";
import { runHappyFlowProviderReassignment } from "../utils/runHappyFlowProviderReassignment";

/** Parameters for {@link useHappyFlowModalSave}. */
interface UseHappyFlowModalSaveParams {
  /** General order details for order context. */
  generalOrderDetails?: GeneralOrderDetails;
  /** Auth-on-file data for J-code analytics. */
  authOnFile?: AuthOnFile;
  /** Coverage slices used for analytics payloads. */
  coverageDetails?: SingleCoverage[];
  /** Mutable ref updated by the provider-reassignment checkbox. */
  selectedProvider: React.MutableRefObject<SelectedProviderRef>;
  /** Auth status captured when the order page loaded. */
  oldAuthStatus?: string;
  /** Callback that logs per-J-code status events. */
  logJCodeStatusEvents: (jCodes: JCodeDetails[], delayMs: number) => void;
}

/** Return value of {@link useHappyFlowModalSave}. */
export interface UseHappyFlowModalSaveResult {
  /** Persists happy-flow save (BO data, assignment, navigation). */
  handleHappyFlowModalSave: (selectedOrderIds?: string[]) => Promise<void>;
  /** True while the happy-flow save sequence is in flight. */
  isLoading: boolean;
}

/**
 * Owns happy-flow save: analytics, comment-RPA persistence, optional provider
 * reassignment after persistence succeeds, and post-save navigation.
 *
 * @param params - Order context and provider selection ref
 * @returns Save handler and loading state
 */
export const useHappyFlowModalSave = (
  params: UseHappyFlowModalSaveParams,
): UseHappyFlowModalSaveResult => {
  const {
    generalOrderDetails,
    authOnFile,
    coverageDetails,
    selectedProvider,
    oldAuthStatus,
    logJCodeStatusEvents,
  } = params;

  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );
  const { data: providerDetails } = useSelector(
    (state: RootState) => state.providerDetails,
  );
  const { globalBoValue } = useBusniessOfficeNotesContext();
  const orgId = getOrgIdForFetchExternalWorklist();

  const { runUpdate: runUpdateGeneralOrderDetails } =
    useUpdateOrderDetails<OrderReassignmentPayload>({
      orderId: generalOrderDetails?.order_id ?? "",
      orgId,
      tableName: SingleOrderDetailsTableName.MEDICAL_PA_ORDER_GENERAL_INFO,
    });
  const { runUpdate: runBatchAssignmentUpdate } =
    useAssignedToUpdate<OrderReassignmentPayload>();

  const [isLoading, setIsLoading] = useState(false);

  const handleHappyFlowModalSave = async (selectedOrderIds?: string[]) => {
    fireMarkAsCompletedAnalytics({
      modalName: ModalNamesForBigQuery.HAPPY_FLOW_MODAL,
      generalOrderDetails,
      authOnFile,
      coverageDetails,
      user,
      oldAuthStatus,
      logJCodeStatusEvents,
    });

    setIsLoading(true);

    try {
      const parentOrderId = generalOrderDetails?.order_id ?? "";

      await markOrderAsCompleted(
        {
          org_id: orgId,
          bo_status: globalBoValue,
          overwrite_rpa_errors: false,
          write_back_reports: null,
        },
        parentOrderId,
        selectedOrderIds,
      );

      if (hasProviderSelection(selectedProvider.current)) {
        const actingProviderName =
          `${providerDetails?.FirstName || ""} ${providerDetails?.LastName || ""}`.trim() ||
          "Unknown Provider";

        await runHappyFlowProviderReassignment({
          parentOrderId,
          selectedOrderIds,
          selectedProvider: selectedProvider.current,
          generalOrderDetails,
          orgId,
          userEmail: user?.email ?? "",
          actingProviderName,
          runBatchAssignmentUpdate,
          runUpdateGeneralOrderDetails,
        });
      }

      navigateToWorklistsAfterMarkComplete(dispatch, navigate);
    } catch (error) {
      showApiError(error, ToastMessages.MARK_AS_COMPLETED_ERROR);
    } finally {
      setIsLoading(false);
    }
  };

  return { handleHappyFlowModalSave, isLoading };
};
